import { useEffect, useState, useCallback } from "react";
import { axiosClient } from "@/lib/axiosClient";
import { Rocket, CheckCircle2, Sparkles, Loader2, LogIn } from "lucide-react"; // Added LogIn icon
import confetti from "canvas-confetti";
import { Link } from "react-router-dom"; // Import Link for navigation

// 🔧 KONFIGURASI URL
const TARGET_URL_REDIRECT = "/"; // Redirect setelah launch

// Tipe Data untuk State Waktu
type TimeLeft = {
    months: number;
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
};

export default function SystemLaunchCountdown() {
    // State Target Date dari API
    const [targetDate, setTargetDate] = useState<Date | null>(null);
    const [isLoadingData, setIsLoadingData] = useState(true);

    // State Hitungan Mundur
    const [timeLeft, setTimeLeft] = useState<TimeLeft>({
        months: 0, days: 0, hours: 0, minutes: 0, seconds: 0
    });

    // [BARU] State untuk mengecek apakah waktu sudah habis
    const [isTimeUp, setIsTimeUp] = useState(false);

    // State Peluncuran (5 Detik)
    const [isLaunching, setIsLaunching] = useState(false);
    const [launchCount, setLaunchCount] = useState(5);

    // ----------------------------------------------------------------
    // 1. FETCH DATA DARI API
    // ----------------------------------------------------------------
    useEffect(() => {
        const fetchConfig = async () => {
            try {
                const response = await axiosClient.get('/config-app');
                const data = 'data' in response.data ? response.data.data : response.data;

                if (data && data.site_status_until) {
                    const isoDateString = data.site_status_until.replace(" ", "T");
                    setTargetDate(new Date(isoDateString));
                }
            } catch (error) {
                console.error("Gagal mengambil konfigurasi countdown:", error);
            } finally {
                setIsLoadingData(false);
            }
        };

        fetchConfig();
    }, []);

    // ----------------------------------------------------------------
    // 2. LOGIKA HITUNG MUNDUR JANGKA PANJANG
    // ----------------------------------------------------------------
    const calculateTimeLeft = useCallback((): TimeLeft => {
        if (!targetDate) return { months: 0, days: 0, hours: 0, minutes: 0, seconds: 0 };

        const now = new Date().getTime();
        const difference = targetDate.getTime() - now;

        // [BARU] Cek apakah waktu sudah habis
        if (difference <= 0) {
            setIsTimeUp(true);
            return { months: 0, days: 0, hours: 0, minutes: 0, seconds: 0 };
        } else {
            setIsTimeUp(false);
        }

        const result: TimeLeft = { months: 0, days: 0, hours: 0, minutes: 0, seconds: 0 };

        if (difference > 0) {
            result.months = Math.floor(difference / (1000 * 60 * 60 * 24 * 30));
            result.days = Math.floor((difference % (1000 * 60 * 60 * 24 * 30)) / (1000 * 60 * 60 * 24));
            result.hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
            result.minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
            result.seconds = Math.floor((difference % (1000 * 60)) / 1000);
        }
        return result;
    }, [targetDate]);

    // Jalankan Timer
    useEffect(() => {
        setTimeLeft(calculateTimeLeft());

        const timer = setInterval(() => {
            setTimeLeft(calculateTimeLeft());
        }, 1000);

        return () => clearInterval(timer);
    }, [targetDate, calculateTimeLeft]);

    // ----------------------------------------------------------------
    // 3. EFEK CONFETTI
    // ----------------------------------------------------------------
    const triggerCelebration = () => {
        const duration = 3000;
        const end = Date.now() + duration;

        (function frame() {
            confetti({ particleCount: 5, angle: 60, spread: 55, origin: { x: 0 }, colors: ['#6366f1', '#8b5cf6', '#ec4899'] });
            confetti({ particleCount: 5, angle: 120, spread: 55, origin: { x: 1 }, colors: ['#6366f1', '#8b5cf6', '#ec4899'] });

            if (Date.now() < end) {
                requestAnimationFrame(frame);
            }
        }());
    };

    // ----------------------------------------------------------------
    // 4. LOGIKA PELUNCURAN (5 DETIK)
    // ----------------------------------------------------------------
    const handleStartLaunch = () => {
        // [BARU] Mencegah klik jika waktu belum habis
        if (!isTimeUp) return;

        setIsLaunching(true);
        setLaunchCount(5);
    };

    useEffect(() => {
        let launchTimer: NodeJS.Timeout;

        if (isLaunching) {
            if (launchCount > 0) {
                launchTimer = setTimeout(() => {
                    setLaunchCount((prev) => prev - 1);
                }, 1000);
            } else if (launchCount === 0) {
                triggerCelebration();
                setTimeout(() => {
                    window.location.href = TARGET_URL_REDIRECT;
                }, 2000);
            }
        }
        return () => clearTimeout(launchTimer);
    }, [isLaunching, launchCount]);

    const customStyles = `
        .circle-progress { stroke-dasharray: 754; stroke-dashoffset: 0; animation: countdown 5s linear forwards; }
        @keyframes countdown { from { stroke-dashoffset: 0; } to { stroke-dashoffset: 754; } }
        @keyframes popIn { 0% { transform: scale(0.5); opacity: 0; } 50% { transform: scale(1.1); opacity: 1; } 100% { transform: scale(1); opacity: 1; } }
        .animate-pop { animation: popIn 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards; }
    `;

    if (isLoadingData) {
        return (
            <div className="flex h-screen w-full items-center justify-center bg-slate-50 dark:bg-slate-950">
                <div className="flex flex-col items-center gap-4">
                    <Loader2 className="w-10 h-10 text-indigo-600 animate-spin" />
                    <p className="text-slate-500 font-medium">Mensinkronisasi Waktu...</p>
                </div>
            </div>
        );
    }

    return (
        <section className="relative flex flex-col items-center justify-center min-h-screen w-full bg-slate-50 dark:bg-slate-950 overflow-hidden font-sans selection:bg-indigo-100">
            <style>{customStyles}</style>

            <div className="absolute inset-0 w-full h-full bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-indigo-100/40 via-slate-50/0 to-slate-50/0 dark:from-indigo-900/20 dark:to-slate-950 -z-10" />

            {/* --- OVERLAY PELUNCURAN (FULLSCREEN) --- */}
            {isLaunching && (
                <div className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-slate-950/95 backdrop-blur-md transition-all duration-700">
                    {launchCount > 0 ? (
                        <div className="relative flex items-center justify-center w-[280px] h-[280px]">
                            <svg className="absolute inset-0 w-full h-full -rotate-90 transform drop-shadow-2xl">
                                <circle cx="140" cy="140" r="120" className="stroke-slate-800 fill-none" strokeWidth="8" />
                                <circle cx="140" cy="140" r="120" className="stroke-indigo-500 fill-none circle-progress" strokeWidth="8" strokeLinecap="round" />
                            </svg>
                            <div key={launchCount} className="relative z-10 flex flex-col items-center">
                                <span className="text-8xl md:text-9xl font-black text-white leading-none animate-pop tabular-nums">
                                    {launchCount}
                                </span>
                            </div>
                        </div>
                    ) : (
                        <div className="flex flex-col items-center animate-pop">
                            <div className="p-6 bg-green-500/20 rounded-full mb-6 backdrop-blur-sm">
                                <CheckCircle2 className="w-24 h-24 text-green-500" />
                            </div>
                            <h2 className="text-4xl md:text-5xl font-bold text-white mb-2">SELAMAT DATANG</h2>
                            <p className="text-slate-400 text-lg">Mengarahkan ke Dashboard...</p>
                        </div>
                    )}
                    {launchCount > 0 && (
                        <p className="mt-12 text-indigo-300 text-sm font-medium tracking-[0.2em] uppercase animate-pulse">
                            Membuka Akses...
                        </p>
                    )}
                </div>
            )}

            {/* --- KONTEN UTAMA --- */}
            <div className={`container mx-auto px-4 transition-all duration-700 ease-in-out ${isLaunching ? 'scale-95 opacity-0 blur-lg' : 'scale-100 opacity-100 blur-0'}`}>
                <div className="flex flex-col items-center text-center max-w-5xl mx-auto space-y-8">

                    {/* Logo Area */}
                    <div className="relative group">
                        <div className="p-4">
                            <img src="/konaspi.png" alt="Logo Konaspi" className="w-24 h-24 md:w-32 md:h-32 object-contain" />
                        </div>
                    </div>

                    <div className="space-y-4">
                        <h2 className="text-sm md:text-base font-semibold text-indigo-600 dark:text-indigo-400 tracking-wider uppercase mb-2">
                            Official Countdown
                        </h2>
                        <h1 className="text-5xl md:text-7xl font-extrabold tracking-tight text-slate-900 dark:text-white drop-shadow-sm">
                            <span className="bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 to-violet-600">KONASPI XII</span>
                        </h1>
                        <p className="text-2xl md:text-3xl font-light text-slate-600 dark:text-slate-300">
                            Universitas Pendidikan Ganesha
                        </p>
                    </div>

                    <div className="w-24 h-1 bg-gradient-to-r from-transparent via-indigo-300 to-transparent rounded-full opacity-50"></div>

                    {/* Countdown Cards */}
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 md:gap-6 w-full max-w-4xl mt-6">
                        {["months", "days", "hours", "minutes", "seconds"].map((unit, i) => (
                            <div key={i} className="group relative flex flex-col items-center justify-center p-4 md:p-6 bg-white/60 dark:bg-slate-800/40 backdrop-blur-sm border border-slate-200/60 dark:border-slate-700/60 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-1">
                                <span className={`text-3xl md:text-5xl font-bold tabular-nums tracking-tight ${isTimeUp ? 'text-green-600 dark:text-green-400' : 'text-slate-800 dark:text-slate-100'}`}>
                                    {String(timeLeft[unit as keyof TimeLeft]).padStart(2, '0')}
                                </span>
                                <span className="text-[10px] md:text-xs font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500 mt-2">
                                    {unit}
                                </span>
                            </div>
                        ))}
                    </div>

                    <div className="pt-8">
                        {/* [MODIFIKASI] Tombol Kondisional */}
                        <button
                            onClick={handleStartLaunch}
                            disabled={!isTimeUp} // Disabled jika belum waktunya
                            className={`group relative inline-flex items-center justify-center gap-3 px-10 py-5 rounded-full overflow-hidden transition-all duration-500 focus:outline-none focus:ring-2 focus:ring-offset-2 
                            ${isTimeUp
                                    ? 'bg-green-600 text-white hover:bg-green-700 hover:scale-105 hover:shadow-[0_0_40px_-10px_rgba(34,197,94,0.5)] cursor-pointer'
                                    : 'bg-slate-200 text-slate-400 dark:bg-slate-800 dark:text-slate-500 cursor-not-allowed grayscale'
                                }`}
                        >
                            {/* Efek shimmer hanya jika aktif */}
                            {isTimeUp && (
                                <div className="absolute inset-0 w-full h-full bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:animate-[shimmer_1.5s_infinite]" />
                            )}

                            <Rocket className={`w-6 h-6 transition-transform ${isTimeUp ? 'group-hover:-translate-y-1 group-hover:translate-x-1' : ''}`} />

                            <span className="text-lg font-semibold tracking-wide">
                                {isTimeUp ? "LAUNCH SYSTEM" : "SYSTEM LOCKED"}
                            </span>

                            {isTimeUp ? (
                                <Sparkles className="w-5 h-5 text-green-200 animate-pulse" />
                            ) : (
                                <div className="w-5 h-5" /> // Spacer agar layout tidak geser
                            )}
                        </button>

                        <p className="mt-6 text-sm text-slate-400 font-light max-w-md mx-auto leading-relaxed">
                            {isTimeUp
                                ? "Sistem siap diluncurkan. Klik tombol di atas untuk masuk."
                                : "Menunggu waktu peluncuran resmi. Tombol akan aktif secara otomatis."
                            }
                        </p>
                    </div>

                </div>
            </div>

            {/* [BARU] Tombol Login Pojok Kanan Bawah */}
            <div className="fixed bottom-6 right-6 z-50">
                <Link
                    to="/login"
                    className="flex items-center justify-center w-12 h-12 bg-white/10 dark:bg-black/20 backdrop-blur-sm border border-slate-200/20 dark:border-slate-700/30 rounded-full text-slate-600 dark:text-slate-400 hover:bg-indigo-600 hover:text-white hover:border-indigo-500 transition-all duration-300 shadow-lg group"
                    title="Login Admin / Panitia"
                >
                    <LogIn className="w-5 h-5 group-hover:ml-1 transition-all" />
                </Link>
            </div>
        </section>
    );
}